// src/lib/month/month.ts
import { formatMonth, type IsoDate, monthEndOf, todayInZone } from "@/lib/time/dates";

/** A calendar month as "YYYY-MM": the Home and Guidance switcher value and URL param. */
export type MonthKey = string;

const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isMonthKey(value: string): boolean {
  return MONTH_KEY.test(value);
}

export function monthKeyOf(date: IsoDate): MonthKey {
  return date.slice(0, 7);
}

export function monthStart(key: MonthKey): IsoDate {
  return `${key}-01`;
}

export function monthEnd(key: MonthKey): IsoDate {
  return monthEndOf(monthStart(key));
}

export function addMonths(key: MonthKey, months: number): MonthKey {
  const index = Number(key.slice(0, 4)) * 12 + (Number(key.slice(5, 7)) - 1) + months;
  const year = Math.floor(index / 12);
  const month = index - year * 12 + 1;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

/** The `span` months ending at `key`, oldest first (Home's 6-month trend). */
export function monthsEndingAt(key: MonthKey, span: number): MonthKey[] {
  return Array.from({ length: span }, (_, i) => addMonths(key, i - span + 1));
}

export function currentMonthKey(now: Date, timeZone?: string): MonthKey {
  return monthKeyOf(todayInZone(now, timeZone));
}

/** The viewer's own zone, for "today" and "this month" in the browser. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const LONG = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "long" });
const SHORT = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short" });
const atUtc = (key: MonthKey) => new Date(`${monthStart(key)}T00:00:00Z`);

/** "October 2026". */
export function monthLabel(key: MonthKey): string {
  return formatMonth(monthStart(key));
}

/** "October", for "October capacity" and "October time". */
export function monthName(key: MonthKey): string {
  return LONG.format(atUtc(key));
}

/** "Oct", under a trend bar. */
export function monthShort(key: MonthKey): string {
  return SHORT.format(atUtc(key));
}

export function yearOf(key: MonthKey): number {
  return Number(key.slice(0, 4));
}

/**
 * Which switcher arrows may move: never past the person's PersonPeriod range, and never while
 * the range is unknown (loading, failed, or a person with no periods). A month outside the
 * range (an old URL) can still step back toward it.
 */
export function monthSteps(
  month: MonthKey,
  min: MonthKey | null,
  max: MonthKey | null,
): { prev: boolean; next: boolean } {
  return { prev: min !== null && month > min, next: max !== null && month < max };
}
